import ButtonGroup from '@pelckmans/elementis/components/button-group';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Section from '@pelckmans/elementis/components/section';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScoreBase } from '../../../../../api/assignment';
import {
  isScorableMedialink,
  isUnlicensedMedialink,
  Medialink,
  validateAndGetLinkFromMedialink,
} from '../../../../../api/medialink';
import { SharedSlideSet } from '../../../../../api/slide-set';
import {
  isScorableLti,
  isSharedUserMaterial,
  SharedUserMaterial,
  validateAndGetLinkFromUserMaterial,
} from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useLicenses from '../../../../../hooks/useLicenses';
import { useSlideSetAnalytics } from '../../../../../hooks/useSlideSetAnalytics';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { buildDoceoUrl } from '../../../../../utils/slideSet';
import ModuleContext from '../../../_contexts/ModuleContext';
import { MaterialIcon, type ContentType } from '../../../components/MaterialIcon';
import SharedMaterialsContext from '../../_contexts/SharedMaterialsContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import useMedialinks from '../../hooks/medialink/useMedialinks';
import useUserMaterial from '../../hooks/user-material/useUserMaterial';
import ScorableProgress from '../ScorableProgress';
import PinnedMaterialButton from '../pinned-material/PinnedMaterialButton';
import AccountContext from '../../../../../contexts/AccountContext';
import MaterialPills from '../MaterialPills';

type SharedMaterialSectionProps = {
  medialinks: Medialink[];
};

type SharedMaterialListItem = {
  id: string;
  key?: string;
  name: string;
  userId?: string;
  materialType: 'user-material' | 'medialink' | 'slide-set';
  contentType?: ContentType;
  score?: ScoreBase;
  isScorable?: boolean;
  unachievable?: boolean;
  href?: string; // to render anchor tags for non-embedded links
  disabled?: boolean;
  assessmentQExercise?: {
    isAdaptive?: boolean;
    isOnTheFly?: boolean;
  };
};

function mapMedialinkToListItem(medialinks: Medialink[]) {
  return medialinks.map(medialink => ({
    id: medialink.id,
    name: medialink.name,
    materialType: 'medialink' as const,
    isScorable: isScorableMedialink(medialink),
    unachievable: medialink.unachievable,
    contentType: medialink.contentType,
    href: validateAndGetLinkFromMedialink(medialink),
    disabled: isUnlicensedMedialink(medialink),
    score: medialink.score,
    assessmentQExercise: medialink.assessmentQExercise,
  }));
}

function filterAndMapSharedMaterialToListItem(
  sharedMaterials: (SharedUserMaterial | SharedSlideSet)[],
  isLicensed: boolean,
): SharedMaterialListItem[] {
  return sharedMaterials.reduce((list, material) => {
    if (material.materialType === 'user-material' && Boolean(material.assignment)) {
      return list;
    }

    const m: SharedMaterialListItem = {
      id: String(material.id),
      name: material.name,
      materialType: material.materialType,
      userId: typeof material.owner === 'string' ? material.owner : material.owner?.id,
      ...('versionId' in material ? { key: `${material.id}-${material.versionId}` } : {}),
      disabled: material.materialType === 'slide-set' && !isLicensed,
    };

    if (material.materialType === 'user-material') {
      if (isScorableLti(material)) {
        m.isScorable = true;
        m.score = material.score;
      } else {
        const link = validateAndGetLinkFromUserMaterial(material);
        if (link) {
          m.href = link;
        }
      }
    }

    list.push(m);
    return list;
  }, [] as SharedMaterialListItem[]);
}

export default function SharedMaterialsSection({ medialinks }: SharedMaterialSectionProps) {
  const { t } = useTranslation();
  const { sharedMaterials } = useContext(SharedMaterialsContext);
  const { captureOpened } = useSlideSetAnalytics();
  const { selectedModule } = useContext(ModuleContext);
  const {
    account: { provider },
  } = useContext(AccountContext);
  const { selectedNode, nodes } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);
  const { openMedialink, fetchMedialinks } = useMedialinks();
  const { openMaterial: openUserMaterial, fetchUserMaterial } = useUserMaterial();
  const { isModuleLicensed } = useLicenses();

  const noneAssignmentMedialinks = medialinks.filter(medialink => !Boolean(medialink.assignments.length));
  const allMaterials = filterAndMapSharedMaterialToListItem(sharedMaterials, isModuleLicensed(selectedModule)).concat(
    mapMedialinkToListItem(noneAssignmentMedialinks),
  );

  const handleOpenMedialink = useCallback(
    // `navigationHandledByAnchor` is set by the href branch, which renders an anchor
    // that already navigates; without it the medialink opens in a second tab.
    async (medialinkId: string, navigationHandledByAnchor = false) => {
      if (!selectedNode || !selectedModule) return;

      const medialink = noneAssignmentMedialinks.find(x => x.id === medialinkId);

      if (medialink) {
        const modal = await openMedialink(
          medialink,
          selectedNode,
          selectedModule,
          isScorableMedialink(medialink) ? fetchMedialinks : undefined,
          undefined,
          selectedModule.isExerciseModule
            ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_SHARED_MATERIAL
            : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MODULE_SHARED_MATERIAL,
          navigationHandledByAnchor,
        );

        if (modal) {
          showModal(modal);
        }
      }
    },
    [selectedNode, selectedModule, noneAssignmentMedialinks, openMedialink, fetchMedialinks, showModal],
  );

  const handleOpenUserMaterial = useCallback(
    async (userMaterialId: string) => {
      if (!selectedNode) return;

      const userMaterial = sharedMaterials.find(x => x.id === userMaterialId);

      if (userMaterial?.materialType === 'user-material' && isSharedUserMaterial(userMaterial)) {
        const modal = await openUserMaterial(
          userMaterial,
          nodes,
          userMaterial.userMaxScore ? fetchUserMaterial : undefined,
          ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MODULE_SHARED_MATERIAL,
        );

        if (modal) {
          showModal(modal);
        }
      }
    },
    [fetchUserMaterial, nodes, openUserMaterial, selectedNode, sharedMaterials, showModal],
  );

  function onSlideSetClick(slideSetId: number) {
    captureOpened(slideSetId, ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MODULE_SHARED_MATERIAL);
  }

  if (allMaterials.length === 0) return null;

  return (
    <Section title={t('PAGES.TOC_LEVEL.SECTIONS.STUDENT.SHARED.TITLE')}>
      {allMaterials.length && (
        <List>
          {allMaterials.map(material => {
            const label =
              material.contentType?.name ||
              t(`PAGES.TOC_LEVEL.SECTIONS.STUDENT.CONTENT_TYPE.${material.materialType.toUpperCase()}`);

            const commonProps = {
              title: material.name,
              pillGroupProps: buildGroupProps(
                <>
                  <Pill key={material.id} prominence="neutral" size="sm" aria-label={label}>
                    {label}
                  </Pill>
                  <MaterialPills
                    key="material-pills"
                    isAdaptive={material?.assessmentQExercise?.isAdaptive}
                    isOnTheFly={material?.assessmentQExercise?.isOnTheFly}
                  />
                </>,
              ),
            };

            switch (material.materialType) {
              case 'slide-set': {
                const materialId = Number(material.id);

                return (
                  <List.Item
                    {...commonProps}
                    key={material.key || materialId}
                    actionArea={
                      <ButtonGroup
                        {...buildGroupProps(
                          <PinnedMaterialButton
                            type="slide-set"
                            materialId={materialId}
                            disabled={material.disabled}
                          />,
                        )}
                        size="sm"
                      />
                    }
                    as="link"
                    href={buildDoceoUrl({ ...material, id: materialId }, provider)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onSlideSetClick(materialId)}
                    icon={
                      <MaterialIcon materialType={material.materialType} isUserSlideSet={Boolean(material.userId)} />
                    }
                    disabled={material.disabled}
                  />
                );
              }

              case 'medialink': {
                return material.href ? (
                  <List.Item
                    {...commonProps}
                    key={material.id}
                    as="link"
                    href={material.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    actionArea={
                      <ButtonGroup
                        {...buildGroupProps(
                          <PinnedMaterialButton
                            type="medialink"
                            materialId={material.id}
                            disabled={material.disabled}
                          />,
                        )}
                        size="sm"
                      />
                    }
                    onClick={() => handleOpenMedialink(material.id, true)}
                    icon={<MaterialIcon materialType={material.materialType} />}
                    disabled={material.disabled}
                  />
                ) : (
                  <List.Item
                    {...commonProps}
                    key={material.id}
                    as="button"
                    contentArea={
                      material.isScorable && (
                        <ScorableProgress unachievable={material.unachievable} score={material.score} />
                      )
                    }
                    actionArea={
                      <ButtonGroup
                        {...buildGroupProps(
                          <PinnedMaterialButton
                            type="medialink"
                            materialId={material.id}
                            disabled={material.disabled}
                          />,
                        )}
                        size="sm"
                      />
                    }
                    onClick={() => handleOpenMedialink(material.id)}
                    icon={<MaterialIcon materialType={material.materialType} />}
                    disabled={material.disabled}
                  />
                );
              }

              case 'user-material': {
                return material.href ? (
                  <List.Item
                    {...commonProps}
                    key={material.id}
                    as="link"
                    href={material.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    actionArea={
                      <ButtonGroup
                        {...buildGroupProps(<PinnedMaterialButton type="user-material" materialId={material.id} />)}
                        size="sm"
                      />
                    }
                    onClick={() => handleOpenUserMaterial(material.id)}
                    icon={<MaterialIcon materialType={material.materialType} />}
                  />
                ) : (
                  <List.Item
                    {...commonProps}
                    key={material.id}
                    as="button"
                    contentArea={material.isScorable && <ScorableProgress score={material.score} />}
                    actionArea={
                      <ButtonGroup
                        {...buildGroupProps(<PinnedMaterialButton type="user-material" materialId={material.id} />)}
                        size="sm"
                      />
                    }
                    onClick={() => handleOpenUserMaterial(material.id)}
                    icon={<MaterialIcon materialType={material.materialType} />}
                  />
                );
              }

              default:
                return null;
            }
          })}
        </List>
      )}
    </Section>
  );
}
